import React, { useEffect, useMemo, useState } from 'react';
import TestSMSButton from '../TestSMSButton';
import ActionButton from '../../../../shared/components/action-button/ActionButton';
import { SMS_CHARACTER_LIMIT } from '../../utils/validation';
import './SimpleSMSTemplateEditor.css';

const DEFAULT_PREVIEW_VALUES = {
  customerName: 'Jordan',
  service: 'Haircut',
  date: 'Apr 4',
  time: '2:30 PM',
  oldDate: 'Apr 2',
  oldTime: '1:00 PM',
  newDate: 'Apr 4',
  newTime: '2:30 PM',
  confirmUrl: 'your-link.example/confirm',
  checkInUrl: 'your-link.example/check-in'
};

const FIELD_EXPLANATIONS = {
  customerName: 'Replaced with the client name.',
  service: 'Replaced with the booked service.',
  date: 'Replaced with the appointment date.',
  time: 'Replaced with the appointment time.',
  oldDate: 'Replaced with the original appointment date.',
  oldTime: 'Replaced with the original appointment time.',
  newDate: 'Replaced with the updated appointment date.',
  newTime: 'Replaced with the updated appointment time.',
  confirmUrl: 'Replaced with the customer confirmation link.',
  checkInUrl: 'Replaced with the self check-in link.',
  portalUrl: 'Replaced with your client portal link.'
};

const buildPreview = (content, previewValues) => {
  return Object.entries(previewValues).reduce(
    (message, [token, value]) => message.replaceAll(`{{${token}}}`, value),
    content
  );
};

const SimpleSMSTemplateEditor = ({
  title,
  description,
  template,
  mergeFields,
  onApply,
  onTestSMS,
  saving
}) => {
  const [draft, setDraft] = useState(template?.content || '');
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    setDraft(template?.content || '');
  }, [template]);

  const previewValues = useMemo(() => {
    const values = { ...DEFAULT_PREVIEW_VALUES };

    mergeFields.forEach((field) => {
      if (!values[field]) {
        values[field] = field.replace(/([A-Z])/g, ' $1').trim();
      }
    });

    return values;
  }, [mergeFields]);

  const previewText = useMemo(() => buildPreview(draft, previewValues), [draft, previewValues]);

  const hasChanges = draft !== (template?.content || '');
  const isOverLimit = draft.length > SMS_CHARACTER_LIMIT;

  return (
    <div className="simple-sms-editor">
      <div className="simple-sms-editor__header">
        <div>
          <h3 className="simple-sms-editor__title">{title}</h3>
          <p className="simple-sms-editor__description">{description}</p>
        </div>

        <div className="simple-sms-editor__actions app-action-toolbar">
          <ActionButton
            type="button"
            onClick={() => setPreviewMode((current) => !current)}
          >
            {previewMode ? 'Edit message' : 'Preview message'}
          </ActionButton>
          <TestSMSButton
            onSendTest={onTestSMS}
            disabled={saving || isOverLimit}
            defaultMessage={draft}
          />
          <ActionButton
            variant="primary"
            type="button"
            disabled={!hasChanges || saving || isOverLimit}
            onClick={() => onApply({ ...template, content: draft })}
          >
            Apply changes
          </ActionButton>
        </div>
      </div>

      <div className="simple-sms-editor__notice">
        Keep messages short. Messages are limited to {SMS_CHARACTER_LIMIT} characters. Available fields:{' '}
        {mergeFields.map((field) => `{{${field}}}`).join(', ')}
      </div>

      <div className="simple-sms-editor__chips">
        {mergeFields.map((field) => (
          <span key={field} className="simple-sms-editor__chip">
            {`{{${field}}}`}
          </span>
        ))}
      </div>

      <div className="simple-sms-editor__tutorial">
        <h4 className="simple-sms-editor__tutorial-title">How these fields work</h4>
        <p className="simple-sms-editor__tutorial-copy">
          Do not replace these yourself. Leave tokens like <code>{'{{customerName}}'}</code> in the message exactly as shown, and the system will swap them for the real appointment details automatically when the SMS sends.
        </p>
        <div className="simple-sms-editor__tutorial-list">
          {mergeFields.map((field) => (
            <div key={field} className="simple-sms-editor__tutorial-item">
              <code>{`{{${field}}}`}</code>
              <span>{FIELD_EXPLANATIONS[field] || 'This placeholder is replaced automatically with the matching appointment value.'}</span>
            </div>
          ))}
        </div>
      </div>

      {previewMode ? (
        <div className="simple-sms-editor__preview">
          <p>{previewText}</p>
        </div>
      ) : (
        <label className="simple-sms-editor__field">
          <span className="simple-sms-editor__label">Message content</span>
          <textarea
            className={`simple-sms-editor__textarea ${isOverLimit ? 'is-error' : ''}`}
            rows={6}
            value={draft}
            maxLength={SMS_CHARACTER_LIMIT}
            onChange={(event) => setDraft(event.target.value)}
          />
          <span className={`simple-sms-editor__helper ${isOverLimit ? 'is-error' : ''}`}>
            {draft.length}/{SMS_CHARACTER_LIMIT} characters
          </span>
        </label>
      )}
    </div>
  );
};

export default SimpleSMSTemplateEditor;
